import './styles.scss'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from 'Components/UIComponent/Button'
import { ProgressSpinner } from 'primereact/progressspinner'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapter_CoffeeWeb'
import { getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import ErrorMessageModal from 'Components/UIComponent/ConfirmationModal'
import { DragIcon } from 'Assets/Icons'

/* eslint-disable react/display-name */
const AddCurrencyModal = memo(({ showModal, setShowModal, fetchForexData, showConfirmation, showError, forexDataLength, userForexSelection }) => {
  const { t } = useTranslation()
  const userDetails = getLocalUserDetails()
  const timerRef = useRef(null)

  const height = 500
  const width = 444

  const [filterText, setFilterText] = useState('')
  const [filteredCountries, setFilteredCountries] = useState([])
  const [selected, setSelected] = useState([]) // Handles currencies to be shown bookmarked
  const [currencyList, setCurrencyList] = useState([])
  const [addCurrencyLoader, setAddCurrencyLoader] = useState(true)
  const [maxCurrencyWarning, setMaxCurrencyWarning] = useState(false)
  const [isAlreadySelected, setIsAlreadySelected] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [overIndex, setOverIndex] = useState(null)
  const [isCheckOwnCountry, setIsCheckOwnCountry] = useState(false)
  const [maxForexCurrencyLength, setMaxForexCurrencyLength] = useState(15)

  useEffect(() => {
    fetchCurrencies()
    getManageCurrenciesLimit()
    setIsAlreadySelected(userForexSelection.some((el) => el.targetCountryIso3 === userDetails.userCountryDetails.iso3)) // To check if user's country was already selected
  }, [])

  useEffect(() => {
    const lowercasedFilter = filterText.toLowerCase()

    const filtered = currencyList.filter((country) => country.countryName?.toLowerCase().includes(lowercasedFilter) || country.targetCurrency?.toLowerCase().includes(lowercasedFilter) || country.searchTag?.toLowerCase().includes(lowercasedFilter))

    setFilteredCountries(filtered)
  }, [filterText, currencyList])

  const getManageCurrenciesLimit = async () => {
    try {
      const response = await apiAdapterCoffeeWeb.getCoffeeQuotesSettings()

      setMaxForexCurrencyLength(response?.data?.returnLst?.forexDataLimit)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchCurrencies = async () => {
    try {
      setAddCurrencyLoader(true)
      const allCurrencies = await apiAdapterCoffeeWeb.getForexDropdownList()
      const currencyListRetrieved = allCurrencies.data.returnLst

      const preSelectedCountries = userForexSelection
        .filter((ele) => ele.targetCurrency !== '$ INDEX')
        .map((ele) => currencyListRetrieved.find((item) => item.targetCountryIso3 === ele.targetCountryIso3))
        .filter((item) => item !== undefined) // Create array of countries from dropdown data in user-forex order
      const preSelectedCodes = preSelectedCountries.map((el) => el.targetCountryIso3) // Create an array with targetCountryIso3
      const removeSet = new Set(preSelectedCodes) // Create a set for faster access of pre selected codes
      const remaining = currencyListRetrieved.filter((ele) => !removeSet.has(ele.targetCountryIso3)) // Data left after user forex list currency removed from dropdown array
      const updatedDropdownList = [...preSelectedCountries, ...remaining] // Append pre selected countries at start and non selected at end.

      setSelected(preSelectedCodes)
      setFilteredCountries(updatedDropdownList)
      setCurrencyList(updatedDropdownList)
    } catch (error) {
      showConfirmation({
        title: t('MANAGE_CURRENCY'),
        message: `<p>${t('FAILED_FETCH_FOREX_CURRENCY_LIST')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('RETRY'),
        dismissButtonLabel: t('CLOSE'),
        buttonAction: 'hard-action',
        onCancel: () => {
          handleCloseAction()
        },
        onConfirm: () => {
          fetchCurrencies()
        }
      })
    } finally {
      setAddCurrencyLoader(false)
    }
  }

  // Reusable function to generate both the forex data and order sequence in a single loop
  const createForexPayloads = (selectedCurrencyObjects, userForexSelection) => {
    const userOrderSequencePayload = []
    const userForexDataPayload = []

    // Loop through selectedCurrencyObjects once
    selectedCurrencyObjects.forEach((country) => {
      userOrderSequencePayload.push(`X:SUSD${country.targetCurrency}`) //  Create symbol-ticker preference array

      // Create user forex data array
      userForexDataPayload.push({
        forexSymbolTicker: `X:SUSD${country.targetCurrency}`,
        baseCurrency: 'USD',
        targetCurrency: country.targetCurrency,
        targetCountryIso3: country.targetCountryIso3
      })
    })

    // Append US Dollar Index with user Forex Data
    const updatedUserForexDataPayload = [
      {
        forexSymbolTicker: userForexSelection[0]?.symbolTicker5,
        baseCurrency: 'USD',
        targetCurrency: 'IDX',
        targetCountryIso3: userForexSelection[0]?.targetCountryIso3
      },
      ...userForexDataPayload
    ]

    return {
      orderSequence: userOrderSequencePayload,
      userForexData: updatedUserForexDataPayload
    }
  }

  const handleSaveAction = async () => {
    try {
      setAddCurrencyLoader(true)
      const selectedCurrencyObjects = currencyList.filter((country) => selected.includes(country.targetCountryIso3))
      const updatedCurrencyPayload = createForexPayloads(selectedCurrencyObjects, userForexSelection)

      await apiAdapterCoffeeWeb.saveSelectedCurrencies(userDetails.id, updatedCurrencyPayload)
      handleCloseAction()

      if (selectedCurrencyObjects?.length > 0) {
        fetchForexData()
      }
    } catch (error) {
      showError({
        title: t('ERROR_TITLE'),
        message: `<p>${t('FAILED_TO_SAVE_USER_FOREX_CURRENCY')}</p><p>${t('TRY_AGAIN_MESSAGE')}</p>`,
        buttonLabel: t('OK')
      })
    } finally {
      setAddCurrencyLoader(false)
    }
  }

  const handleCloseAction = () => {
    setShowModal(false)
    setSelected([])
  }

  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    setOverIndex(index)
  }

  const handleDrop = (index) => {
    if (draggedIndex === null) return

    const updatedList = [...currencyList]
    const [movedItem] = updatedList.splice(draggedIndex, 1)

    updatedList.splice(index, 0, movedItem)

    setCurrencyList(updatedList)
    setFilteredCountries(updatedList) // keep filtered view in sync
    setDraggedIndex(null)
    setOverIndex(null)
  }

  const toggleSelection = (code) => {
    const isOwnCountry = code === userDetails.userCountryDetails.iso3
    const isAlreadySelectedCode = selected.includes(code)

    // Prevent own country from being deselected
    if (isOwnCountry && isAlreadySelectedCode) {
      setIsCheckOwnCountry(true)
      setTimeout(() => setIsCheckOwnCountry(false), 10000)

      return
    }

    // Prevent adding new if already at max
    if (!isAlreadySelectedCode && selected.length >= maxForexCurrencyLength) {
      setMaxCurrencyWarning(true)

      return
    }

    let updatedSelected

    if (isAlreadySelectedCode) {
      updatedSelected = selected.filter((c) => c !== code)
    } else {
      updatedSelected = [...selected, code]
    }

    setSelected(updatedSelected)

    // Separate into selected + unselected groups
    const selectedItems = currencyList.filter((c) => updatedSelected.includes(c.targetCountryIso3))

    const unselectedItems = currencyList.filter((c) => !updatedSelected.includes(c.targetCountryIso3)).sort((a, b) => a.countryName.localeCompare(b.countryName)) // alphabetical for unselected

    // Merge back: selected first, then unselected (alphabetical)
    const reorderedList = [...selectedItems, ...unselectedItems]

    setCurrencyList(reorderedList)
    setFilteredCountries(reorderedList)

    setMaxCurrencyWarning(updatedSelected.length > maxForexCurrencyLength)
  }

  const handleOwnCountryClick = (isCountryAlreadySelected, isOwnCountry, countryCode) => {
    // If another country clicked, clear timer
    if (!isOwnCountry) {
      setIsCheckOwnCountry(false)
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      toggleSelection(countryCode)

      return
    }

    // Own country clicked
    if (isCountryAlreadySelected && isOwnCountry) {
      // If timer already running, don't start another
      if (!timerRef.current) {
        setIsCheckOwnCountry(true)
        timerRef.current = setTimeout(() => {
          setIsCheckOwnCountry(false)
          timerRef.current = null
        }, 10000)
      }

      return
    }

    // Selecting own country for the first time
    setIsCheckOwnCountry(false)
    toggleSelection(countryCode)
  }

  const addCurrencyModalContent = () => (
    <>
      {addCurrencyLoader ? (
        <div className="add-currency-loader">
          <ProgressSpinner className="progress-spinner" strokeWidth="4" animationDuration="2s" />
        </div>
      ) : (
        <div className="custom-country-selector">
          <input type="text" placeholder="Search Currency" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="filter-input" />

          {!filteredCountries?.length && <div>No Currency Available</div>}
          <div className="country-list">
            {filteredCountries?.map((country, index) => {
              const isSelected = selected.includes(country.targetCountryIso3)
              const isOwnCountry = country.targetCountryIso3 === userDetails.userCountryDetails.iso3

              return (
                <div key={country.targetCountryIso3} className={`country-row country-list ${overIndex === index ? 'drag-over' : ''}`} draggable={isSelected} onDragStart={isSelected ? () => handleDragStart(index) : undefined} onDragOver={isSelected ? (e) => handleDragOver(e, index) : undefined} onDrop={isSelected ? () => handleDrop(index) : undefined} onClick={() => toggleSelection(country.targetCountryIso3)}>
                  <div className="country-row-details">
                    <span className="country-flag">
                      <img src={`https://coffeeweb.s3.ap-south-1.amazonaws.com/Masters/Flags/${country.targetCountryIso3}.svg`} alt="Flag" data-testid="country-flag-image" />
                    </span>
                    <div>
                      <span className="currency-name">{country.targetCurrency}</span>
                      <span className="country-name">- {country.countryName}</span>
                    </div>
                  </div>

                  <div className="icons-container">
                    <span
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOwnCountryClick(isSelected, isOwnCountry, country.targetCountryIso3)
                      }}
                      className={`bookmark-icon ${isAlreadySelected && isOwnCountry ? 'disabled-icon' : ''}`}
                    >
                      {isSelected ? <i className="pi pi-minus-circle bookmark-icon" /> : <i className="pi pi-plus-circle bookmark-icon" />}
                    </span>
                    {/* Drag icon only for selected countries */}
                    {isSelected && <DragIcon data-testid="drag-icon" />}
                  </div>
                </div>
              )
            })}
          </div>
          {maxCurrencyWarning && <div style={{ padding: '8px 0', color: '#ff1100' }}>You can add up to {maxForexCurrencyLength} currencies. If you'd like to add another, you'll need to remove one.</div>}
          {isCheckOwnCountry && <div style={{ color: 'red', paddingTop: '8px' }}>You can't remove your own country</div>}
        </div>
      )}
    </>
  )

  const footerContent = (
    <div className="add-currency-dialog-footer">
      <Button buttonType="CANCEL" onButtonClick={() => handleCloseAction()} />
      <Button disabled={addCurrencyLoader} buttonType={'SAVE'} onButtonClick={() => handleSaveAction()} />
    </div>
  )

  return (
    <>
      <div className="add-currency-modal">
        <DialogUIModal title={t('MANAGE_CURRENCY')} titlePosition="center" showModal={showModal} contentHeight={`${height}px`} contentWidth={`${width}px`} bodyContent={addCurrencyModalContent} handleClose={() => handleCloseAction()} testId="accordion-popup" footerContent={footerContent} customClassName="forex-modal" />
      </div>
    </>
  )
})

export default ErrorMessageModal(AddCurrencyModal)
